// This is where you apply for a goodleap loan & submit the application...
import React, { useCallback, useState } from "react";
import axios from "axios";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import ssl from "../../../assets/images/ssl.svg";
import encryption from "../../../assets/images/encryption.svg";
import checkmark from "../../../assets/images/checkmark.svg";
import { useError } from "../../../hooks";
import { Select, Input } from "../../../shared/components";

import sungageApi from "../api/sungage-api";

const SungageStatus = {
  APPROVED: "Approved",
  APPROVED_STIPS_PENDING: "Approved (stips pending)",
  UNDER_REVIEW: "Under review",
  INELIGIBLE: "Ineligible for financing",
  NOT_YET_SUBMITTED: "Not yet submitted",
};

const SungageQualify = ({
  name,
  customer,
  email,
  street,
  city,
  state,
  zip,
  setSungageView,
  viewApps,
  setViewApps,
  loanA,
  loanAmount,
  proposal,
  reRoof,
  reRoofAmount,
  batteryCost,
  sungageApplication,
  setSungageApplication,
  systemCost,
  downPayment,
  activeUser,
  recValue,
  rebate,
}) => {
  const listOfStates = require("us-state-converter");
  const only50 = listOfStates.only50();
  const error = useError();
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({
    firstName: name.split(" ")[0],
    lastName: name.split(" ")[1],
    email: email,
    street,
    city,
    state: listOfStates.abbr(state),
    zip,
    coApply: false,
    coFirstName: "",
    coLastName: "",
    coEmail: "",
    loanAmount: loanAmount,
  });

  /**
   * handler function to update form state from an onChange event
   */
  const handleFormStateChange = (event) => {
    const { name, value } = event.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  /**
   * handler function to manually update form state from a passed key and value
   */
  const updateFormState = (key, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  /**
   * handler function to submit the application
   */
  const handleSubmit = async () => {
    try {
      error.clear();
      setSubmitting(true);

      const projectId = proposal?.projectId + proposal?.propId;

      // 1. create sungage project
      const payload = {
        id: projectId,
        username: activeUser.username,
        primaryApplicant: {
          firstName: formState.firstName,
          lastName: formState.lastName,
          email: formState.email,
          language: "English",
        },
        projectAddress: {
          street: formState.street,
          city: formState.city,
          state: formState.state,
          zip: formState.zip,
        },
        loan: {
          term: loanA?.loanTerm,
          rate: loanA?.loanInterest,
          ...(loanA?.ppaEscalator != null && loanA?.ppaEscalator > 0
            ? { dppType: "0", dppProduct: "Escalator" }
            : { dppType: "36", dppProduct: "Classic DPP", dppEscalator: loanA?.ppaEscalator }),
        },
        projectDetails: {
          solarCost: Math.round(
            systemCost - (reRoofAmount || 0) - (batteryCost || 0)
          ),
          solarSize: proposal.systemSize / 1000,
          downPayment: +downPayment,
        },
        // include co-applicant if applicable
        ...(formState.coApply
          ? {
              secondaryApplicant: {
                firstName: formState.coFirstName,
                lastName: formState.coLastName,
                email: formState.coEmail,
              },
            }
          : {}),
      };

      // manually add re-roof cost if applicable
      if (reRoof) {
        payload.projectDetails.roofCost = Math.round(+reRoofAmount);
      }

      // manually add battery amount if applicable
      if (batteryCost) {
        payload.projectDetails.batteryCost = batteryCost;
        payload.projectDetails.batterySize = 13.5;
      }

      // manuall add illinois solar rebate
      if (state === "Illinois") {
        let solarRebate = 0;

        if (rebate > 0) {
          solarRebate += rebate;
        }

        if (recValue?.afterEnergy > 0) {
          solarRebate += Math.round(recValue.afterEnergy);
        }

        if (solarRebate > 0) {
          payload.projectDetails.solarRebate = {
            amount: solarRebate,
            paidToHomeowner: true,
          };
        }
      }

      const { sungageId } = await sungageApi.createProject(payload);

      // 2. save the Sungage Id to our database
      await sungageApi.addData({
        projectId,
        sungageId,
      });

      // 3. get the credit application
      const { url } = await sungageApi.submitApplication({
        username: activeUser.username,
        projectId,
        sungageId,
      });

      // 4. get the Sungage project
      const project = await sungageApi.getProject({
        username: activeUser.username,
        projectId,
        sungageId,
      });

      // 5. set sungage application and open the credit application url
      if (project) {
        setSungageApplication(project);
      }
      if (url) {
        window.open(url, "_blank");
      }
    } catch (e) {
      error.set({
        message: `An error occurred while submitting your application. ${e.message}`,
        autoDismiss: false,
      });
      console.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // boolean check if all inputs are filled out
  const isDisabled = !(
    formState.firstName &&
    formState.lastName &&
    formState.email &&
    formState.street &&
    formState.city &&
    formState.state &&
    formState.zip &&
    (formState.coApply
      ? formState.coFirstName && formState.coLastName && formState.coEmail
      : true)
  );

  // show application view if sungageId and projectId are present
  if (sungageApplication)
    return (
      <Applications
        activeUser={activeUser}
        setSungageView={setSungageView}
        setViewApps={setViewApps}
        sungageApplication={sungageApplication}
      />
    );

  return (
    <>
      <div className="container" style={{ position: "relative" }}>
        {submitting && (
          <div className="submission-overlay">
            <h3>Submitting data</h3>
            <CircularProgress />
          </div>
        )}
        <div className="custContainer">
          {/* HEADER */}
          <div className="row justify-content-between">
            <div className="col">
              {loanA && !viewApps && (
                <h4>{`Sungage ${loanA?.loanTerm} YR ${loanA?.loanInterest}% APR`}</h4>
              )}
            </div>
            <div className="col" align="end">
              <div className="qualyText header back">
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => {
                    setSungageView(false);
                    setViewApps(false);
                  }}
                >
                  Back
                </Button>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <div className="qualyText header">
                Confirm Customer Information
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <div className="qualyText">
                Please provide a valid email address you can access and a mobile
                phone number to receive instant notifications about your project
                and keep it running smoothly.
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="row">
            <div className="col-sm">
              <div className="qualyText holders">First Name</div>
              <Input
                name="firstName"
                placeholder="Legal First Name"
                value={formState["firstName"]}
                onChange={handleFormStateChange}
              />
            </div>
            <div className="col">
              <div>
                <div className="qualyText holders">Last Name</div>
              </div>
              <Input
                name="lastName"
                placeholder="Legal Last Name"
                value={formState["lastName"]}
                onChange={handleFormStateChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm">
              <div>
                <div className="qualyText holders">Email Address</div>
              </div>
              <Input
                name="email"
                placeholder="email@email.com"
                value={formState["email"]}
                onChange={handleFormStateChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-4">
              <div>
                <div className="qualyText holders">Street Address</div>
              </div>
              <input
                name="street"
                className="custInput qualy"
                placeholder="Street"
                value={formState["street"]}
                onChange={handleFormStateChange}
              ></input>
            </div>
            <div className="col-sm-4">
              <div>
                <div className="qualyText holders">City</div>
              </div>
              <input
                name="city"
                className="custInput qualy"
                placeholder="City"
                value={formState["city"]}
                onChange={handleFormStateChange}
              ></input>
            </div>
            <div className="col-sm-2 col">
              <div>
                <div className="qualyText holders">State</div>
                <Select
                  name="state"
                  className="custInput qualy moz"
                  value={formState["state"]}
                  onChange={handleFormStateChange}
                  options={only50.map((state) => ({
                    value: state.usps,
                    label: state.usps,
                  }))}
                />
              </div>
            </div>
            <div className="col-sm-2 col">
              <div>
                <div className="qualyText holders">Zip Code</div>
              </div>
              <Input
                name="zip"
                placeholder="00000"
                label={formState["zip"]}
                value={formState["zip"]}
                onChange={handleFormStateChange}
              />
            </div>
          </div>
          <div
            className="row"
            onClick={(e) => {
              updateFormState("coApply", !formState["coApply"]);
            }}
          >
            <div className="col-sm-1 col-2">
              <div
                className={
                  formState["coApply"] ? "checkBox checked" : "checkBox"
                }
              >
                <img src={checkmark}></img>
              </div>
            </div>
            <div className="col">
              <div className="qualyText check">Add Co-Applicant</div>
            </div>
          </div>
          {formState["coApply"] && (
            <>
              <div className="row">
                <div className="col-sm">
                  <div className="qualyText holders">
                    Co-Applicant First Name
                  </div>
                  <Input
                    name="coFirstName"
                    placeholder="Legal First Name"
                    value={formState["coFirstName"]}
                    onChange={handleFormStateChange}
                  />
                </div>
                <div className="col">
                  <div>
                    <div className="qualyText holders">
                      Co-Applicant Last Name
                    </div>
                  </div>
                  <Input
                    name="coLastName"
                    placeholder="Legal Last Name"
                    value={formState["coLastName"]}
                    onChange={handleFormStateChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm">
                  <div>
                    <div className="qualyText holders">
                      Co-Applicant Email Address
                    </div>
                  </div>
                  <Input
                    name="coEmail"
                    placeholder="email@email.com"
                    value={formState["coEmail"]}
                    onChange={handleFormStateChange}
                  />
                </div>
              </div>
            </>
          )}

          {/* SUBMISSION */}
          <ErrorMessage show={error.show} message={error.message} />
          <div className="row justify-content-center">
            <div className="col">
              <button
                className={
                  !isDisabled ? "qualyButton confirm" : "qualyButton disabled"
                }
                disabled={isDisabled}
                onClick={() => !isDisabled && handleSubmit()}
              >
                <>
                  <img className="img"></img>
                  {"Apply through Sungage"}
                </>
              </button>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col">
              <div className="qualyText bottom">
                Submitting this pre-qualification will <span>not affect</span>{" "}
                your credit score.
              </div>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col">
              <div className="qualyText bottom bold">
                YOUR INFORMATION IS SECURED BY{" "}
                <img className="img" src={ssl}></img> SSL{" "}
                <img className="img" src={encryption}></img> 128-BIT ENCRYPTION
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

function Applications({
  setSungageView,
  setViewApps,
  sungageApplication,
  activeUser,
}) {
  const [project, setProject] = useState(sungageApplication);

  const fetchProject = async () => {
    setProject(null);

    try {
      const sungageProject = await sungageApi.getProject({
        username: activeUser.username,
        projectId: sungageApplication.id,
        sungageId: sungageApplication.sungageId,
      });
      setProject(sungageProject);
    } catch (error) {
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case SungageStatus.APPROVED:
        return "green";
      case SungageStatus.APPROVED_STIPS_PENDING:
        return "#ffcb3b";
      case SungageStatus.UNDER_REVIEW:
        return "#ffcb3b";
      case SungageStatus.INELIGIBLE:
        return "red";
      case SungageStatus.NOT_YET_SUBMITTED:
        return "gray";
      default:
        return "gray";
    }
  };

  const getFormattedAddress = useCallback(() => {
    if (!project) return "";

    const { projectAddress } = project;
    return `${projectAddress.street}, ${projectAddress.city}, ${projectAddress.state} ${projectAddress.zip}`;
  }, [project]);
  const getFormattedDate = useCallback(() => {
    if (!project) return "";

    return new Date(project.projectDetails.createdDate).toLocaleDateString();
  }, [project]);

  return (
    <div className="container">
      <div className="custContainer">
        <div className="row justify-content-between">
          <div className="col">
            <h3 className="menuFont qualy">Application Status</h3>
          </div>
          <div className="col" align="end">
            <div
              onClick={() => {
                setSungageView(false);
                setViewApps(false);
              }}
              className="qualyText header back"
            >
              {"< Back"}
            </div>
          </div>
        </div>
        {!project && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </div>
        )}
        {project && (
          <div className="application-card">
            <div className="card-body">
              <div className="card-col">
                <h4 className="card-title">{`${project.primaryApplicant.firstName} ${project.primaryApplicant.lastName}`}</h4>
                <p>
                  Submitted on: <span>{getFormattedDate()}</span>
                </p>
                <h6>{getFormattedAddress()}</h6>
              </div>
              <div className="card-status" align="end">
                <h5>Status: </h5>
                <p
                  style={{
                    color: getStatusColor(project.applicationDetails?.status),
                  }}
                >
                  {project.applicationDetails?.status || "N/A"}
                </p>
              </div>
            </div>
            <div className="card-footer">
              <button
                className="btn-outline"
                onClick={() => window.open(project?.projectUrl, "_blank")}
              >
                View Application
              </button>
              <button onClick={fetchProject}>
                <span className="icon">⟳</span> Refresh Status
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Error message component
function ErrorMessage({ show, message }) {
  if (!show) return null;

  return (
    <div className="row justify-content-center">
      <div className="col" align="center">
        <div className="sungageErrorMessage">{message}</div>
        {message.includes("Could not create project") && (
          <div className="sungageErrorTips">
            <h5>Tips</h5>
            <ul>
              <li>Double check you have signed your Sungage code of conduct</li>
              <li>Make sure you have an active Sungage account</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default SungageQualify;
